import { useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@hooks/use_translation.ts";

export type SaveErrors = Record<string, string[]>;

const normalizeErrors = (errors: Record<string, string | string[]> = {}): SaveErrors =>
  Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key, Array.isArray(value) ? value : [value]]),
  );

const useSectionSave = (path = "/quick_apply") => {
  const queryClient = useQueryClient();
  const showFlash = useShowFlash();
  const { t } = useTranslation("user");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<SaveErrors>({});

  const save = async (formData: FormData): Promise<boolean> => {
    setSaving(true);
    setErrors({});

    try {
      await axios.patch(path, formData, { headers: { Accept: "application/json" } });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      showFlash(t("profile_updated.success"), "success");
      return true;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        const data = e.response.data as { errors?: Record<string, string | string[]> };
        setErrors(normalizeErrors(data?.errors));
      } else {
        showFlash(t("defaultError"), "error");
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving, errors };
};

export default useSectionSave;
